import type {
  LoginRequest,
  RefreshTokenResponse,
  SignupRequest,
  SocialLoginRequest,
  TokenResponse,
  VerifyPasswordRequest,
} from "../../domain/types/auth";
import { RefreshTokenResponseSchema, TokenResponseSchema } from "../types/auth";
import type { AuthRepository } from "../repositories/auth-repository";
import { userApiService } from "../../infrastructure/api/user-api-service";
import { authApiService } from "../../infrastructure/api/auth-api-service";
import { AppError } from "../../shared/utils/error-handler";
import { StorageService } from "../../infrastructure/storage/storage-service";
import { STORAGE_KEYS } from "../../shared/constants/storage";

export class AuthUseCase {
  private storageService: StorageService;

  constructor(private authRepository: AuthRepository) {
    this.storageService = new StorageService();
  }

  async verifyPassword(request: VerifyPasswordRequest): Promise<boolean> {
    try {
      return await this.authRepository.verifyPassword(request);
    } catch (error) {
      return false;
    }
  }

  async socialLogin(request: SocialLoginRequest): Promise<TokenResponse> {
    try {
      const tokenResponse = await this.authRepository.socialLogin(request);

      // Bearer 토큰 처리
      // : Bearer 접두사 제거하고 저장
      let accessToken = tokenResponse.accessToken;
      if (accessToken && accessToken.startsWith("Bearer ")) {
        accessToken = accessToken.substring(7); // "Bearer " 제거
      }

      // 토큰을 로컬스토리지에 먼저 저장
      if (accessToken) {
        this.storageService.setItem(STORAGE_KEYS.ACCESS_TOKEN, accessToken);
      }
      if (tokenResponse.refreshToken) {
        this.storageService.setItem(
          STORAGE_KEYS.REFRESH_TOKEN,
          tokenResponse.refreshToken,
        );
      }

      // 사용자 정보 조회
      const userResponse = await userApiService.getUserInfo();
      const user = userResponse.data;

      if (!user) {
        throw new Error("사용자 정보 조회 실패.");
      }

      return {
        userId: user.id,
        accessToken: tokenResponse.accessToken,
        refreshToken: tokenResponse.refreshToken,
        companyId: user.companyId,
      };
    } catch (error) {
      console.error("Social login failed:", error);
      throw new AppError(
        String(error) || "소셜 로그인 처리에 실패했습니다.",
        "SOCIAL_LOGIN_FAILED",
        error,
      );
    }
  }

  async signup(request: SignupRequest): Promise<boolean> {
    try {
      return await this.authRepository.signup(request);
    } catch (error) {
      throw new AppError(
        String(error) || "회원가입에 실패했습니다.",
        "SIGNUP_FAILED",
        error,
      );
    }
  }

  async reissue(request: RefreshTokenResponse): Promise<RefreshTokenResponse> {
    try {
      const response = await this.authRepository.reissue(request.refreshToken);
      return RefreshTokenResponseSchema.parse(response);
    } catch (error) {
      throw new AppError(
        String(error) || "토큰 갱신에 실패했습니다.",
        "TOKEN_REFRESH_FAILED",
        error,
      );
    }
  }

  async login(
    request: LoginRequest,
    rememberMe: Boolean,
  ): Promise<TokenResponse> {
    try {
      // rememberMe 설정에 따라 저장 위치 결정 (localStorage vs sessionStorage)
      this.storageService.setPersistentMode(!!rememberMe);

      const tokenResponse = await this.authRepository.login(request);

      // Bearer 토큰 처리 - Bearer 접두사 제거하고 저장
      let accessToken = tokenResponse.accessToken;
      if (accessToken && accessToken.startsWith("Bearer ")) {
        accessToken = accessToken.substring(7); // "Bearer " 제거
      }

      // 토큰을 저장 (rememberMe에 따라 localStorage 또는 sessionStorage에 저장)
      if (accessToken) {
        this.storageService.setItem(STORAGE_KEYS.ACCESS_TOKEN, accessToken);
      }
      // 리프레시 토큰은 항상 저장 (자동 토큰 갱신을 위해 필수)
      if (tokenResponse.refreshToken) {
        this.storageService.setItem(
          STORAGE_KEYS.REFRESH_TOKEN,
          tokenResponse.refreshToken,
        );
      }

      // 사용자 정보 조회
      const userResponse = await userApiService.getUserInfo();
      const user = userResponse.data;

      return tokenResponse;
    } catch (error) {
      console.error("Login failed:", error);
      throw new AppError("로그인에 실패했습니다.", "LOGIN_FAILED", error);
    }
  }

  /**
   * 로그인 API 밖에서 받은 토큰(초대 수락, 워크스페이스 선택)을 저장한다.
   * login/socialLogin 과 같은 규칙: Bearer 접두사를 떼고, 리프레시 토큰은 항상 저장.
   */
  saveTokens(tokenResponse: TokenResponse): void {
    let accessToken = tokenResponse.accessToken;
    if (accessToken && accessToken.startsWith("Bearer ")) {
      accessToken = accessToken.substring(7);
    }
    if (accessToken) {
      this.storageService.setItem(STORAGE_KEYS.ACCESS_TOKEN, accessToken);
    }
    if (tokenResponse.refreshToken) {
      this.storageService.setItem(STORAGE_KEYS.REFRESH_TOKEN, tokenResponse.refreshToken);
    }
  }

  async selectWorkspace(selectionToken: string, userId: number): Promise<TokenResponse> {
    try {
      const response = await authApiService.selectWorkspace(selectionToken, userId);
      return TokenResponseSchema.parse(response.data);
    } catch (error) {
      throw new AppError(
        String(error) || "워크스페이스 선택에 실패했습니다.",
        "WORKSPACE_SELECT_FAILED",
        error,
      );
    }
  }

  async logout(): Promise<void> {
    try {
      await this.authRepository.logout();
    } catch (error) {
      // 로그아웃은 서버 실패해도 클라이언트에서 처리
      console.warn(
        "Server logout failed, but proceeding with client logout:",
        error,
      );
    }
  }
}
